import { useEffect, useState, useCallback } from "react";
import { backendClient } from "../../../../../../api/backend";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  alpha,
  useTheme,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  ArrowLeft,
  Send,
  Pause,
  Play,
  Users,
  Mail,
  MailOpen,
  MousePointerClick,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  TrendingUp,
  BarChart3,
  ListChecks,
  Activity,
  RefreshCw,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  FlaskConical,
} from "lucide-react";
import ABTestPanel from "./ABTestPanel";
import {
  fetchMarketingCampaign,
  fetchCampaignStats,
  fetchCampaignFunnel,
  fetchCampaignSends,
  fetchMarketingSequences,
  pauseMarketingCampaign,
  resumeMarketingCampaign,
} from "../../../../../../redux/actions/marketingAction";
import OrganizationFiche from "../Organisations/OrganizationFiche/OrganizationFiche";

const STATUS_COLORS = {
  draft: "default",
  scheduled: "info",
  sending: "warning",
  paused: "secondary",
  sent: "primary",
  completed: "success",
};

const SEND_STATUS_CONFIG = {
  queued: { color: "#6b7280", icon: Clock, label: "Queued" },
  sent: { color: "#3b82f6", icon: Send, label: "Sent" },
  delivered: { color: "#10b981", icon: CheckCircle2, label: "Delivered" },
  opened: { color: "#8b5cf6", icon: MailOpen, label: "Opened" },
  clicked: { color: "#f59e0b", icon: MousePointerClick, label: "Clicked" },
  bounced: { color: "#ef4444", icon: XCircle, label: "Bounced" },
  failed: { color: "#ef4444", icon: AlertTriangle, label: "Failed" },
};

/* ──────────── Stat Card ──────────── */
const StatCard = ({ icon: Icon, label, value, suffix, color, subValue }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: "12px",
        border: `1px solid ${theme.palette.divider}`,
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        flex: 1,
        minWidth: 140,
      }}
    >
      <Box
        sx={{
          width: 36, height: 36, borderRadius: "10px",
          bgcolor: alpha(color, 0.1),
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}
      >
        <Icon size={18} color={color} />
      </Box>
      <Box>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.7rem" }}>
          {label}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.1rem", lineHeight: 1.2 }}>
          {value}{suffix}
        </Typography>
        {subValue && (
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.65rem" }}>
            {subValue}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

/* ──────────── Funnel Bar ──────────── */
const FunnelBar = ({ label, count, percentage, color, maxCount }) => {
  const theme = useTheme();
  const widthPct = maxCount > 0 ? (count / maxCount) * 100 : 0;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "0.8rem" }}>{label}</Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: "0.8rem" }}>
          {count.toLocaleString()} ({percentage.toFixed(1)}%)
        </Typography>
      </Box>
      <Box sx={{ height: 8, borderRadius: 4, bgcolor: alpha(theme.palette.divider, 0.3), overflow: "hidden" }}>
        <Box sx={{ height: "100%", width: `${widthPct}%`, borderRadius: 4, bgcolor: color, transition: "width 0.5s ease" }} />
      </Box>
    </Box>
  );
};

/* ──────────── Info Item ──────────── */
const InfoItem = ({ label, value }) => {
  const theme = useTheme();
  return (
    <Box>
      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {label}
      </Typography>
      {typeof value === "string" || typeof value === "number" ? (
        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "0.8rem" }}>{value}</Typography>
      ) : value}
    </Box>
  );
};

/* ════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ════════════════════════════════════════════════════════════ */
const CampaignDetail = ({ campaignId }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { currentCampaign, campaignStats, campaignFunnel, campaignSends, sequences } =
    useSelector((s) => s.marketing);

  const [activeTab, setActiveTab] = useState(0);
  const [sendsPage, setSendsPage] = useState(0);
  const [loadingData, setLoadingData] = useState(true);
  const [sendsLoading, setSendsLoading] = useState(false);
  const [sendsFilter, setSendsFilter] = useState("all");
  const [sendsSort, setSendsSort] = useState(null); // null | "asc" | "desc"
  const [ficheOrgId, setFicheOrgId] = useState(null);

  const campaign = currentCampaign;

  /* ── Load all data ── */
  const loadData = useCallback(async () => {
    setLoadingData(true);
    try {
      await Promise.all([
        dispatch(fetchMarketingCampaign(campaignId)),
        dispatch(fetchCampaignStats(campaignId)),
        dispatch(fetchCampaignFunnel(campaignId)),
        dispatch(fetchCampaignSends(campaignId, { page: 1, page_size: 50 })),
        dispatch(fetchMarketingSequences(campaignId)),
      ]);
    } finally {
      setLoadingData(false);
    }
  }, [dispatch, campaignId]);

  useEffect(() => { loadData(); }, [loadData]);

  /* ── Reload sends on page/filter/sort change ── */
  useEffect(() => {
    setSendsLoading(true);
    dispatch(fetchCampaignSends(campaignId, {
      page: sendsPage + 1,
      page_size: 50,
      ...(sendsFilter !== "all" && { status: sendsFilter }),
      ...(sendsSort && { sort_by: "lead_score", sort_order: sendsSort }),
    })).finally(() => setSendsLoading(false));
  }, [dispatch, campaignId, sendsPage, sendsFilter, sendsSort]);

  const handleBack = () => navigate("/admin/marketing/campaigns");

  const handlePause = async () => {
    await dispatch(pauseMarketingCampaign(campaignId));
    dispatch(fetchMarketingCampaign(campaignId));
  };

  const handleResume = async () => {
    await dispatch(resumeMarketingCampaign(campaignId));
    dispatch(fetchMarketingCampaign(campaignId));
  };

  /* ── Loading state ── */
  if (loadingData && !campaign) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!campaign) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>Campaign not found</Typography>
        <Button onClick={handleBack} sx={{ mt: 2 }} startIcon={<ArrowLeft size={16} />}>Back</Button>
      </Box>
    );
  }

  const stats = campaignStats || {};
  const funnel = campaignFunnel?.steps || [];
  const sends = campaignSends?.items || [];
  const sendsTotal = campaignSends?.total || 0;
  const seqs = sequences || [];

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2, flexShrink: 0 }}>
        <IconButton onClick={handleBack} size="small" sx={{ border: `1px solid ${theme.palette.divider}` }}>
          <ArrowLeft size={18} />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.1rem" }}>{campaign.name}</Typography>
            <Chip label={campaign.status} size="small" color={STATUS_COLORS[campaign.status] || "default"}
              sx={{ borderRadius: "6px", fontSize: "0.7rem", fontWeight: 600 }} />
          </Box>
          {campaign.description && (
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: "0.8rem", mt: 0.25 }}>
              {campaign.description}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={loadData} size="small"><RefreshCw size={16} /></IconButton>
          </Tooltip>
          {campaign.status === "sending" && (
            <Button size="small" variant="outlined" startIcon={<Pause size={14} />} onClick={handlePause}>Pause</Button>
          )}
          {campaign.status === "paused" && (
            <Button size="small" variant="contained" startIcon={<Play size={14} />} onClick={handleResume}>Resume</Button>
          )}
        </Box>
      </Box>

      {/* ── Campaign Info Bar ── */}
      <Paper elevation={0} sx={{ p: 2, borderRadius: "12px", border: `1px solid ${theme.palette.divider}`, mb: 2.5, flexShrink: 0 }}>
        <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          <InfoItem label="Channel" value={campaign.channel} />
          <InfoItem label="Sender" value={`${campaign.sender_name || ""} <${campaign.sender_email || "N/A"}>`} />
          <InfoItem label="Created" value={campaign.created_at ? new Date(campaign.created_at).toLocaleString() : "—"} />
          {campaign.started_at && <InfoItem label="Started" value={new Date(campaign.started_at).toLocaleString()} />}
          {campaign.scheduled_at && <InfoItem label="Scheduled" value={new Date(campaign.scheduled_at).toLocaleString()} />}
          {campaign.completed_at && <InfoItem label="Completed" value={new Date(campaign.completed_at).toLocaleString()} />}
          <InfoItem label="Segments" value={
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
              {(campaign.segments || []).map((seg) => (
                <Chip key={seg.id} label={seg.name} size="small"
                  sx={{ borderRadius: "6px", fontSize: "0.65rem", height: 20,
                    bgcolor: seg.color ? alpha(seg.color, 0.1) : alpha(theme.palette.primary.main, 0.1),
                    color: seg.color || theme.palette.primary.main }} />
              ))}
            </Box>
          } />
          {campaign.utm_campaign && <InfoItem label="UTM Campaign" value={campaign.utm_campaign} />}
        </Box>
      </Paper>

      {/* ── Tabs ── */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 0, flexShrink: 0 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}
          sx={{ minHeight: 36, "& .MuiTab-root": { minHeight: 36, textTransform: "none", fontWeight: 500, fontSize: "0.85rem", py: 0 } }}>
          <Tab icon={<BarChart3 size={15} />} iconPosition="start" label="Overview" />
          <Tab icon={<ListChecks size={15} />} iconPosition="start" label="Recipients" />
          <Tab icon={<Activity size={15} />} iconPosition="start" label="Sequence" />
          <Tab icon={<TrendingUp size={15} />} iconPosition="start" label="Funnel" />
          <Tab icon={<FlaskConical size={15} />} iconPosition="start" label="A/B Tests" />
        </Tabs>
      </Box>

      {/* ── Tab Content (scrollable) ── */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", pt: 2.5 }}>
        {activeTab === 0 && <OverviewTab stats={stats} campaign={campaign} />}
        {activeTab === 1 && (
          <RecipientsTab sends={sends} sendsTotal={sendsTotal} sendsPage={sendsPage}
            setSendsPage={setSendsPage} sendsFilter={sendsFilter} setSendsFilter={setSendsFilter}
            sendsSort={sendsSort} setSendsSort={setSendsSort}
            campaignId={campaignId} campaignStatus={campaign?.status} sendsLoading={sendsLoading}
            onOrgClick={setFicheOrgId} />
        )}
        {activeTab === 2 && <SequenceTab sequences={seqs} campaignId={campaignId} stats={stats} />}
        {activeTab === 3 && <FunnelTab funnel={funnel} />}
        {activeTab === 4 && <ABTestPanel campaignId={campaignId} />}
      </Box>

      <OrganizationFiche
        open={Boolean(ficheOrgId)}
        onClose={() => setFicheOrgId(null)}
        orgId={ficheOrgId}
      />
    </Box>
  );
};

/* ════════════════════════════════════════════════════════════
   OVERVIEW TAB
   ════════════════════════════════════════════════════════════ */
const OverviewTab = ({ stats, campaign }) => {
  const theme = useTheme();
  return (
    <Box>
      {/* Stat cards */}
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 3 }}>
        <StatCard icon={Users} label="Recipients" value={stats.total_recipients || campaign.total_recipients || 0} color="#6366f1" />
        <StatCard icon={Send} label="Sent" value={stats.total_sent || 0} color="#f59e0b" />
        <StatCard icon={CheckCircle2} label="Delivered" value={stats.total_delivered || 0} color="#10b981" />
        <StatCard icon={MailOpen} label="Opened" value={stats.total_opened || 0}
          suffix={stats.open_rate ? ` (${stats.open_rate.toFixed(1)}%)` : ""} color="#8b5cf6" />
        <StatCard icon={MousePointerClick} label="Clicked" value={stats.total_clicked || 0}
          suffix={stats.click_rate ? ` (${stats.click_rate.toFixed(1)}%)` : ""} color="#f59e0b" />
        <StatCard icon={XCircle} label="Bounced" value={stats.total_bounced || 0}
          suffix={stats.bounce_rate ? ` (${stats.bounce_rate.toFixed(1)}%)` : ""} color="#ef4444" />
      </Box>

      {/* Sends by status */}
      {stats.sends_by_status && Object.keys(stats.sends_by_status).length > 0 && (
        <Paper elevation={0} sx={{ p: 2, borderRadius: "12px", border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, fontSize: "0.85rem" }}>Sends by Status</Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {Object.entries(stats.sends_by_status).map(([status, count]) => {
              const cfg = SEND_STATUS_CONFIG[status] || { color: "#6b7280", label: status };
              return (
                <Chip key={status} label={`${cfg.label}: ${count}`} size="small"
                  sx={{ borderRadius: "6px", fontSize: "0.75rem", bgcolor: alpha(cfg.color, 0.1), color: cfg.color, fontWeight: 600 }} />
              );
            })}
          </Box>
        </Paper>
      )}

      {/* Daily breakdown */}
      {stats.daily_sends && stats.daily_sends.length > 0 && (
        <Paper elevation={0} sx={{ p: 2, borderRadius: "12px", border: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, fontSize: "0.85rem" }}>Daily Activity</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Date</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Sent</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Opened</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Clicked</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats.daily_sends.map((day) => (
                  <TableRow key={day.date}>
                    <TableCell sx={{ fontSize: "0.8rem" }}>{day.date}</TableCell>
                    <TableCell align="right" sx={{ fontSize: "0.8rem" }}>{day.sent}</TableCell>
                    <TableCell align="right" sx={{ fontSize: "0.8rem" }}>{day.opened}</TableCell>
                    <TableCell align="right" sx={{ fontSize: "0.8rem" }}>{day.clicked}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

/* ════════════════════════════════════════════════════════════
   RECIPIENTS TAB
   ════════════════════════════════════════════════════════════ */
const RecipientsTab = ({ sends, sendsTotal, sendsPage, setSendsPage, sendsFilter, setSendsFilter, sendsSort, setSendsSort, campaignId, campaignStatus, sendsLoading, onOrgClick }) => {
  const theme = useTheme();
  const isDraft = ["draft", "scheduled"].includes(campaignStatus);

  // Eligible contacts state (for draft/scheduled campaigns)
  const [eligibleContacts, setEligibleContacts] = useState([]);
  const [eligibleTotal, setEligibleTotal] = useState(0);
  const [eligiblePage, setEligiblePage] = useState(0);
  const [eligibleLoading, setEligibleLoading] = useState(false);

  useEffect(() => {
    if (!isDraft || !campaignId) return;
    const load = async () => {
      setEligibleLoading(true);
      try {
        const res = await backendClient.getMarketingCampaignEligibleContacts(campaignId, { page: eligiblePage + 1, page_size: 50 });
        setEligibleContacts(res.items || []);
        setEligibleTotal(res.total || 0);
      } catch (e) { console.error(e); }
      finally { setEligibleLoading(false); }
    };
    load();
  }, [isDraft, campaignId, eligiblePage]);

  // Draft mode: show eligible contacts
  if (isDraft) {
    return (
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <Chip label={`${eligibleTotal} eligible contacts`} size="small"
            sx={{ borderRadius: "6px", fontSize: "0.75rem", fontWeight: 600, bgcolor: alpha("#6366f1", 0.1), color: "#6366f1" }} />
        </Box>
        <Paper elevation={0} sx={{ borderRadius: "12px", border: `1px solid ${theme.palette.divider}`, overflow: "hidden", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
          <TableContainer sx={{ flex: 1, overflow: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>Contact</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>Company</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>Segments</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {eligibleLoading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}><CircularProgress size={20} /></TableCell>
                  </TableRow>
                ) : eligibleContacts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4, color: theme.palette.text.secondary }}>
                      No eligible contacts in linked segments
                    </TableCell>
                  </TableRow>
                ) : (
                  eligibleContacts.map((c) => (
                    <TableRow key={c.id} hover>
                      <TableCell sx={{ fontSize: "0.8rem", fontWeight: 500 }}>
                        {[c.first_name, c.last_name].filter(Boolean).join(" ") || "—"}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.8rem" }}>{c.email}</TableCell>
                      <TableCell sx={{ fontSize: "0.8rem" }}>{c.company || "—"}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {(c.segments || []).map((name, i) => (
                            <Chip key={i} label={name} size="small"
                              sx={{ borderRadius: "6px", fontSize: "0.6rem", height: 18, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }} />
                          ))}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {eligibleTotal > 50 && (
            <TablePagination component="div" count={eligibleTotal} page={eligiblePage}
              onPageChange={(_, p) => setEligiblePage(p)} rowsPerPage={50} rowsPerPageOptions={[50]} />
          )}
        </Paper>
      </Box>
    );
  }

  // Active/completed mode: show sends with status filters
  const statusFilters = ["all", "queued", "delivered", "opened", "clicked", "bounced", "failed"];

  return (
    <Box>
      {/* Filter chips */}
      <Box sx={{ display: "flex", gap: 0.5, mb: 2, flexWrap: "wrap" }}>
        {statusFilters.map((s) => {
          const isActive = sendsFilter === s;
          const cfg = SEND_STATUS_CONFIG[s];
          return (
            <Chip key={s} label={s === "all" ? "All" : cfg?.label || s} size="small"
              onClick={() => { setSendsFilter(s); setSendsPage(0); }}
              sx={{
                borderRadius: "6px", fontSize: "0.7rem", fontWeight: isActive ? 600 : 400,
                bgcolor: isActive ? alpha(cfg?.color || theme.palette.primary.main, 0.12) : "transparent",
                color: isActive ? cfg?.color || theme.palette.primary.main : theme.palette.text.secondary,
                border: `1px solid ${isActive ? alpha(cfg?.color || theme.palette.primary.main, 0.3) : theme.palette.divider}`,
              }}
            />
          );
        })}
      </Box>

      <Paper elevation={0} sx={{ borderRadius: "12px", border: `1px solid ${theme.palette.divider}`, overflow: "hidden", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
        <TableContainer sx={{ flex: 1, overflow: "auto" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>Contact</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>Organization</TableCell>
                <TableCell
                  sx={{ fontWeight: 600, fontSize: "0.75rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5", cursor: "pointer", userSelect: "none", "&:hover": { color: theme.palette.primary.main } }}
                  onClick={() => {
                    setSendsSort((prev) => prev === null ? "desc" : prev === "desc" ? "asc" : null);
                    setSendsPage(0);
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    Score
                    {sendsSort === null && <ArrowUpDown size={13} style={{ opacity: 0.4 }} />}
                    {sendsSort === "desc" && <ArrowDown size={13} />}
                    {sendsSort === "asc" && <ArrowUp size={13} />}
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>Segment</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>Sent At</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>Opened At</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>Clicked At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sendsLoading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              ) : sends.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4, color: theme.palette.text.secondary }}>
                    No sends found
                  </TableCell>
                </TableRow>
              ) : (
                sends.map((send) => {
                  const cfg = SEND_STATUS_CONFIG[send.status] || SEND_STATUS_CONFIG.queued;
                  const StatusIcon = cfg.icon;
                  return (
                    <TableRow
                      key={send.id} hover
                      onClick={() => {
                        if (send.organization_id && onOrgClick) onOrgClick(send.organization_id);
                      }}
                      sx={{ cursor: send.organization_id ? "pointer" : "default", "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.04) } }}
                    >
                      <TableCell sx={{ fontSize: "0.8rem", fontWeight: 500 }}>{send.contact_name || "—"}</TableCell>
                      <TableCell sx={{ fontSize: "0.8rem" }}>{send.contact_email || "—"}</TableCell>
                      <TableCell sx={{ fontSize: "0.75rem" }}>
                        {send.organization_name || "—"}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.75rem", fontWeight: 600 }}>{send.lead_score ?? "—"}</TableCell>
                      <TableCell>
                        <Chip icon={<StatusIcon size={12} />} label={cfg.label} size="small"
                          sx={{ borderRadius: "6px", fontSize: "0.7rem", fontWeight: 600,
                            bgcolor: alpha(cfg.color, 0.1), color: cfg.color, "& .MuiChip-icon": { color: cfg.color } }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.75rem" }}>
                        {send.segment_name || "—"}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.75rem", color: theme.palette.text.secondary }}>
                        {send.sent_at ? new Date(send.sent_at).toLocaleString() : "—"}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.75rem", color: theme.palette.text.secondary }}>
                        {send.opened_at ? new Date(send.opened_at).toLocaleString() : "—"}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.75rem", color: theme.palette.text.secondary }}>
                        {send.clicked_at ? new Date(send.clicked_at).toLocaleString() : "—"}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {sendsTotal > 50 && (
          <TablePagination component="div" count={sendsTotal} page={sendsPage}
            onPageChange={(_, p) => setSendsPage(p)} rowsPerPage={50} rowsPerPageOptions={[50]} />
        )}
      </Paper>
    </Box>
  );
};

/* ════════════════════════════════════════════════════════════
   SEQUENCE TAB
   ════════════════════════════════════════════════════════════ */
const SequenceTab = ({ sequences, campaignId, stats }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const campaign = useSelector((s) => s.marketing.currentCampaign);

  useEffect(() => {
    if (campaignId) dispatch(fetchMarketingSequences(campaignId));
  }, [dispatch, campaignId]);

  if (!sequences || sequences.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 4, borderRadius: "12px", border: `1px solid ${theme.palette.divider}`, textAlign: "center" }}>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          No sequences configured for this campaign.
        </Typography>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block", mt: 1 }}>
          Go to the Sequences tab to create email sequences for this campaign.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {sequences.map((seq) => (
        <Paper key={seq.id} elevation={0} sx={{ p: 2, borderRadius: "12px", border: `1px solid ${theme.palette.divider}` }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{seq.name}</Typography>
              {seq.segment_name && (
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>Segment: {seq.segment_name}</Typography>
              )}
            </Box>
            <Chip label={`${(seq.steps || []).length} steps`} size="small" variant="outlined"
              sx={{ borderRadius: "6px", fontSize: "0.7rem" }} />
          </Box>

          {/* Steps Timeline */}
          <Box sx={{ position: "relative", pl: 3 }}>
            {(seq.steps || []).sort((a, b) => a.step_order - b.step_order).map((step, idx) => {
              const isLast = idx === (seq.steps || []).length - 1;
              // Determine if step has been sent: campaign started + delay_days has passed
              const isSent = campaign?.started_at && campaign.status !== "draft" &&
                new Date() > new Date(new Date(campaign.started_at).getTime() + step.delay_days * 86400000);
              const dotColor = isSent ? "#10b981" : theme.palette.primary.main;
              return (
                <Box key={step.id} sx={{ position: "relative", pb: isLast ? 0 : 2.5 }}>
                  {/* Connector line */}
                  {!isLast && (
                    <Box sx={{ position: "absolute", left: -19, top: 20, bottom: 0, width: 2,
                      bgcolor: alpha(isSent ? "#10b981" : theme.palette.primary.main, 0.2) }} />
                  )}
                  {/* Dot */}
                  <Box sx={{ position: "absolute", left: -24, top: 4, width: 12, height: 12, borderRadius: "50%",
                    bgcolor: dotColor, border: `2px solid ${theme.palette.background.paper}`,
                    boxShadow: `0 0 0 2px ${alpha(dotColor, 0.3)}` }} />
                  {/* Step card */}
                  <Box sx={{ p: 1.5, borderRadius: "8px", border: `1px solid ${theme.palette.divider}`,
                    bgcolor: alpha(theme.palette.divider, 0.05) }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                      <Chip label={`J+${step.delay_days}`} size="small"
                        sx={{ borderRadius: "6px", fontSize: "0.65rem", fontWeight: 700, height: 20,
                          bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }} />
                      {step.send_time && (
                        <Chip icon={<Clock size={10} />} label={step.send_time} size="small" variant="outlined"
                          sx={{ borderRadius: "6px", fontSize: "0.65rem", height: 20, "& .MuiChip-icon": { ml: 0.5 } }} />
                      )}
                      {step.ab_test_enabled && (
                        <Chip label="A/B" size="small" color="warning"
                          sx={{ borderRadius: "6px", fontSize: "0.65rem", height: 20 }} />
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "0.8rem" }}>{step.subject}</Typography>
                    {step.ab_test_enabled && step.subject_b && (
                      <Typography variant="body2" sx={{ fontSize: "0.75rem", color: theme.palette.text.secondary, mt: 0.25 }}>
                        B: {step.subject_b}
                      </Typography>
                    )}
                    {step.template_name && (
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.7rem" }}>
                        Template: {step.template_name}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Paper>
      ))}
    </Box>
  );
};

/* ════════════════════════════════════════════════════════════
   FUNNEL TAB
   ════════════════════════════════════════════════════════════ */
const FunnelTab = ({ funnel }) => {
  const theme = useTheme();
  const funnelColors = ["#6366f1", "#3b82f6", "#10b981", "#8b5cf6", "#f59e0b"];

  if (!funnel || funnel.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 4, borderRadius: "12px", border: `1px solid ${theme.palette.divider}`, textAlign: "center" }}>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          No funnel data available yet. Start sending to see the funnel.
        </Typography>
      </Paper>
    );
  }

  const maxCount = Math.max(...funnel.map((s) => s.count), 1);

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: "12px", border: `1px solid ${theme.palette.divider}`, mb: 2.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, fontSize: "0.85rem" }}>Campaign Funnel</Typography>
        {funnel.map((step, idx) => (
          <FunnelBar key={step.label} label={step.label} count={step.count}
            percentage={step.percentage} color={funnelColors[idx % funnelColors.length]} maxCount={maxCount} />
        ))}
      </Paper>

      {/* Conversion rates */}
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: "12px", border: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, fontSize: "0.85rem" }}>Conversion Rates</Typography>
        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
          {funnel.length > 1 && funnel.slice(1).map((step, idx) => {
            const prevCount = funnel[idx].count;
            const convRate = prevCount > 0 ? ((step.count / prevCount) * 100).toFixed(1) : "0.0";
            return (
              <Box key={step.label}>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.65rem" }}>
                  {funnel[idx].label} → {step.label}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1rem", color: funnelColors[(idx + 1) % funnelColors.length] }}>
                  {convRate}%
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Paper>
    </Box>
  );
};

export default CampaignDetail;
